/**
 * ColumnTagCloud — chip-облако для выбора колонок.
 *
 * P0.3 2026-07-28: источник колонок = raw_metadata.original_columns
 * (как в SearchColumnTagCloud) + filterUserVisibleColumns для отсечения
 * технических ключей (blob/sha256/total_rows/error_count и т.д.).
 */
import { useTranslation } from 'react-i18next';
import { Columns, Check } from 'lucide-react';

import { useGenerationStore } from '@/store/generationStore';
import { useDatasetPreview } from '@/hooks/useFiles';
import { filterUserVisibleColumns } from '@/lib/columnDenylist';
import { cn } from '@/lib/utils';

export function ColumnTagCloud() {
  const { t } = useTranslation();
  const datasetId = useGenerationStore((s) => s.datasetId);
  const selected = useGenerationStore((s) => s.selectedColumns);
  const toggle = useGenerationStore((s) => s.toggleColumn);
  const { data: preview } = useDatasetPreview(datasetId);

  const rawMeta = preview?.dataset?.raw_metadata as { original_columns?: string[] } | undefined;
  const rawCols: string[] =
    rawMeta?.original_columns ??
    (preview?.rows?.[0] ? Object.keys(preview.rows[0].raw_data) : []);
  const columns = filterUserVisibleColumns(rawCols);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <Columns className="w-4 h-4 text-surface-400" />
        <span className="text-sm font-semibold">
          {t('generation.columns.title', { defaultValue: 'Columns' })}
        </span>
        <span className="text-xs text-surface-500 ml-2">
          {selected.length}/{columns.length} selected
        </span>
      </div>
      {columns.length === 0 ? (
        <p className="text-sm text-surface-500">
          {t('generation.columns.empty', { defaultValue: 'No columns available' })}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {columns.map((col) => {
            const isSel = selected.includes(col);
            return (
              <button
                key={col}
                onClick={() => toggle(col)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors',
                  isSel
                    ? 'bg-primary-500/15 border-primary-500/30 text-primary-200'
                    : 'bg-surface-800/40 border-surface-700 text-surface-400 hover:border-surface-600',
                )}
              >
                {isSel && <Check className="w-3.5 h-3.5" />}
                {col}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
